"use client";

import { FaUserCircle } from "react-icons/fa";
import { BiUserCircle } from "react-icons/bi";
import { signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

import useLoginModal from "@/app/hooks/useLoginModal";
import useRegisterModal from "@/app/hooks/useRegisterModal";
import useRentModal from "@/app/hooks/useRentModal";
import useLiveaboardModal from "@/app/hooks/useLiveaboardModal";
import { SafeChat } from "@/app/types";
import GiftCardsMenuItem from "@/app/gifts/components/GiftCardsMenuItem";
import { useNotifications } from "@/app/context/NotificationContext";
import { getDiveCenterUrl } from "@/app/utils/slugify";

interface UserMenuProps {
  isOpen: boolean;
  toggleOpen: () => void;
  chatsData: {
    chats: SafeChat[] | null;
    totalPages: number;
    currentPage: number;
  };
}

type MenuItemType = {
  label: string;
  path?: string;
  data?: object;
  style?: string;
  className?: string;
  onClick?: () => void;
};

const UserMenu: React.FC<UserMenuProps> = ({
  isOpen,
  toggleOpen,
  chatsData,
}) => {
  const router = useRouter();
  const loginModal = useLoginModal();
  const registerModal = useRegisterModal();
  const rentModal = useRentModal();
  const liveaboardModal = useLiveaboardModal();
  const { unreadCount } = useNotifications();
  const { data: session, status } = useSession();
  const menuRef = useRef<HTMLDivElement>(null);

  const user = session?.user;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        if (isOpen) {
          toggleOpen();
        }
      }
    };

    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        toggleOpen();
        menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", escape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", escape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleOpen]);

  const handleMessagesClick = () => {
    const userData = { type: user?.userType };
    router.push(
      `/chats?userData=${encodeURIComponent(JSON.stringify(userData))}&chatType=PROPERTY&showArchived=false`,
    );
    toggleOpen();
  };

  const handleSignOut = () => {
    const callbackUrl = process.env.NEXTAUTH_URL;
    signOut({ callbackUrl });
  };

  const handleMenuItemClick = (action: () => void) => {
    action();
    toggleOpen();
  };

  const createUrlWithParams = (path: string, data: object) =>
    `${path}?userData=${encodeURIComponent(JSON.stringify(data))}`;

  const userData = {
    type: user?.userType,
  };

  const menuItems: MenuItemType[] = user
    ? [
        ...(user.userType === "Diver"
          ? [
              {
                label: "Edit profile",
                path: `/editprofile/${user.id}`,
                data: userData,
              },
              {
                label: "My trips",
                path: `/trips?tab=upcoming`,
                data: userData,
              },
              {
                label: "My favorites",
                path: "/favorites",
                data: userData,
              },
              {
                label: "Messages",
                onClick: handleMessagesClick,
              },
              ...(user.role === "ADMIN"
                ? [
                    {
                      label: "Reservations management",
                      path: "/adm/reservations",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Gift card orders",
                      path: "/adm/gifts",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Payouts",
                      path: "/adm/payouts",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Invoices",
                      path: "/adm/invoices",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Liveaboards management",
                      path: "/adm/liveaboards",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Centers management",
                      path: "/adm/center-management",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Divers management",
                      path: "/adm/divers",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Listings management",
                      path: "/adm/listings-management",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Availability dashboard",
                      path: "/adm/availability",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Reviews management",
                      path: "/adm/reviews",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Chats",
                      path: "/adm/chats",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Coupons",
                      path: "/adm/coupons",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Users count",
                      path: "/adm/count",
                      style: "bg-gray-300",
                    },
                    {
                      label: "Statistics",
                      path: "/adm/stats",
                      style: "bg-gray-300",
                    },
                  ]
                : []),
            ]
          : []),
        ...(user.userType === "Center"
          ? [
              {
                label: "Edit profile",
                path: `/editprofile/${user.id}`,
                data: userData,
              },
              {
                label: "My profile",
                path: user.diveCenter?.id
                  ? getDiveCenterUrl(user.name, user.diveCenter.id)
                  : "/",
                data: userData,
              },
              {
                label: "My reservations",
                path: "/reservations",
                data: userData,
              },
              { label: "My listings", path: "/courses", data: userData },
              {
                label: "My liveaboards",
                path: "/dive-center/liveaboards",
                data: userData,
              },
              {
                label: "Messages",
                onClick: handleMessagesClick,
              },
              {
                label: "Payments & Payouts",
                path: `/payments-payouts/${user.diveCenter?.id}`,
                data: userData,
              },
              {
                label: "Invoices",
                path: "/invoices",
                data: userData,
              },
              {
                label: "List your courses",
                onClick: rentModal.onOpen,
                style: "bg-cyan-700",
              },
              {
                label: "List your liveaboard",
                onClick: liveaboardModal.onOpen,
                style: "bg-cyan-700",
              },
            ]
          : []),
        { label: "Logout", onClick: handleSignOut },
      ]
    : [
        { label: "Login", onClick: loginModal.onOpen },
        { label: "Sign up", onClick: registerModal.onOpen },
      ];

  const accountItems = menuItems.filter(
    (item) => !item.path?.startsWith("/adm/") && item.label !== "Logout",
  );
  const giftIndex =
    accountItems.findIndex((item) => item.label === "My favorites") >= 0
      ? accountItems.findIndex((item) => item.label === "My favorites")
      : Math.max(
          0,
          accountItems.findIndex((item) => item.label === "My trips"),
        );
  const adminItems = menuItems.filter((item) => item.path?.startsWith("/adm/"));
  const renderItem = (item: MenuItemType) => (
    <button
      type="button"
      key={item.path || item.label}
      onClick={() =>
        item.onClick
          ? handleMenuItemClick(item.onClick)
          : handleMenuItemClick(() =>
              router.push(
                item.data
                  ? createUrlWithParams(item.path!, item.data)
                  : item.path!,
              ),
            )
      }
      className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50 hover:text-cyan-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-700"
    >
      {item.label}
    </button>
  );
  return (
    <div className="relative" ref={menuRef}>
      <div className="relative">
        <button
          type="button"
          onClick={toggleOpen}
          aria-label={
            user ? `Account: ${user.name || user.email}` : "Account menu"
          }
          aria-expanded={isOpen}
          aria-controls="account-dropdown"
          className="flex cursor-pointer flex-row items-center gap-2 rounded-full border border-gray-200 bg-white px-2 py-1.5 transition hover:shadow-md md:pl-2.5 md:pr-3"
        >
          {status === "unauthenticated" ? (
            <BiUserCircle size={20} />
          ) : (
            <FaUserCircle size={20} />
          )}
          <span className="hidden max-w-[120px] truncate text-sm text-gray-700 md:block">
            {user?.name}
          </span>
        </button>
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-600 text-xs text-white">
            {unreadCount}
          </span>
        )}
      </div>
      {isOpen && (
        <div
          id="account-dropdown"
          className="absolute right-0 z-50 mt-3 max-h-[75dvh] w-[min(88vw,300px)] overflow-y-auto rounded-2xl border border-gray-100 bg-white p-2 shadow-xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {status === "loading" ? (
            <p className="px-3 py-3 text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              {accountItems.map((item, index) => (
                <div key={item.path || item.label}>
                  {renderItem(item)}
                  {user && index === giftIndex && (
                    <GiftCardsMenuItem signedIn onNavigate={toggleOpen} />
                  )}
                </div>
              ))}
              {adminItems.length > 0 && (
                <div className="my-1 rounded-xl bg-slate-100">
                  {adminItems.map(renderItem)}
                </div>
              )}
              {user && (
                <button
                  type="button"
                  onClick={() => handleMenuItemClick(handleSignOut)}
                  className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  Log out
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UserMenu;
